import { useQueryClient } from "@tanstack/react-query";
import type { User } from "kubb";
import {
  getUserProfileUserProfileGetQueryKey,
  useUpdateUserProfileEndpointUserProfilePatch,
} from "kubb/hooks/UserHooks";
import { useCallback } from "react";
import { useProfile } from "./useProfile";

// Feature IDs must follow the format: YYYY-MM-DD-feature-name
// e.g., 2026-01-15-export-units
const FEATURE_ID_PATTERN = /^\d{4}-\d{2}-\d{2}-[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Validates that a feature ID follows the required format.
 * @param featureId - The feature ID to validate
 * @returns true if valid, throws Error if invalid
 */
function validateFeatureId(featureId: string): void {
  if (!FEATURE_ID_PATTERN.test(featureId)) {
    throw new Error(
      `Invalid feature ID: "${featureId}". Feature IDs must follow format: YYYY-MM-DD-feature-name (e.g., 2026-01-15-export-units)`,
    );
  }
}

interface UseFeatureAcknowledgementResult {
  /** Whether the feature has been acknowledged by the user */
  isAcknowledged: boolean;
  /** Function to acknowledge the feature */
  acknowledge: () => void;
  /** Whether the acknowledgement is being persisted */
  isLoading: boolean;
  /** Whether the user profile is still loading */
  isProfileLoading: boolean;
}

/**
 * Hook to manage feature acknowledgement state for dismissible banners.
 *
 * Uses the user profile to check if a feature has been acknowledged,
 * and provides a function to acknowledge (dismiss) the feature.
 *
 * @param featureId - Unique identifier for the feature in format YYYY-MM-DD-feature-name (e.g., "2026-01-15-export-units")
 * @returns Object with acknowledgement state and acknowledge function
 */
export function useFeatureAcknowledgement(
  featureId: string,
): UseFeatureAcknowledgementResult {
  // Validate feature ID format on hook initialization
  validateFeatureId(featureId);
  const profile = useProfile();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } =
    useUpdateUserProfileEndpointUserProfilePatch();

  // Check if the feature has been acknowledged
  const isAcknowledged = Boolean(
    profile?.profile?.acknowledgements?.[featureId],
  );

  // Check if user profile is still loading
  const isProfileLoading = profile === undefined;

  const acknowledge = useCallback(async () => {
    const acknowledgedAt = new Date().toISOString();

    // Optimistically merge the acknowledgement into the cached profile so the
    // banner hides immediately and stays hidden across navigation/unmount, even
    // before the PATCH resolves. The cache is the source of truth (useProfile).
    queryClient.setQueryData(
      getUserProfileUserProfileGetQueryKey(),
      (old: { data: User } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: {
            ...old.data,
            profile: {
              ...old.data.profile,
              acknowledgements: {
                ...old.data.profile?.acknowledgements,
                [featureId]: acknowledgedAt,
              },
            },
          },
        };
      },
    );

    try {
      await mutateAsync({
        data: {
          acknowledgements: {
            [featureId]: acknowledgedAt,
          },
        },
      });

      // Mark the profile stale without an eager refetch (refetchType: "none").
      // useProfile is an active observer, so a default invalidate would refetch
      // immediately and could clobber the optimistic acknowledgement if the GET
      // raced the just-persisted PATCH. The next natural mount picks up fresh data.
      queryClient.invalidateQueries({
        queryKey: getUserProfileUserProfileGetQueryKey(),
        refetchType: "none",
      });
    } catch (error) {
      // On error, keep the banner hidden (graceful degradation): the user already
      // clicked "Got it", so don't annoy them. The optimistic cache update
      // persists for this session intentionally.
      console.error("Failed to persist feature acknowledgement:", error);
    }
  }, [featureId, mutateAsync, queryClient]);

  return {
    isAcknowledged,
    acknowledge,
    isLoading: isPending,
    isProfileLoading,
  };
}
